import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import apiService from '../services/api'

const JoinCommunity = () => {
  return (
    <AuthGuard
      requireAuth={true}
      title="কমিউনিটিতে যোগ দিন"
      description="আমন্ত্রণ লিঙ্ক দিয়ে কমিউনিটিতে যোগ দিন।"
    >
      {({ user, logout, openAuth }) => <JoinCommunityContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const JoinCommunityContent = ({ user, logout, openAuth }) => {
  const { inviteCode } = useParams()
  const navigate = useNavigate()
  const [community, setCommunity] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    loadCommunity()
  }, [inviteCode])

  const loadCommunity = async () => {
    try {
      setLoading(true)
      const data = await apiService.getCommunityByInviteCode(inviteCode)
      setCommunity(data.community)
    } catch (err) {
      console.error('Failed to load community:', err)
      setError(err.message || 'কমিউনিটি লোড করতে ব্যর্থ হয়েছে।')
    } finally {
      setLoading(false)
    }
  }

  const handleJoinCommunity = async () => {
    if (!confirm(`${community.name} কমিউনিটিতে যোগ দিতে চান?`)) return

    try {
      setJoining(true)
      const result = await apiService.joinViaInviteCode(inviteCode)
      alert(result.message)
      navigate('/communities')
    } catch (err) {
      console.error('Failed to join community:', err)
      alert(err.message || 'কমিউনিটিতে যোগদান ব্যর্থ হয়েছে।')
    } finally {
      setJoining(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex flex-col">
      <Navbar user={user} logout={logout} openAuth={openAuth} />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/communities')}
              className="flex items-center space-x-2"
            >
              <Icon name="ArrowLeft" className="w-4 h-4" />
              <span>ফিরে যান</span>
            </Button>
          </div>

          {loading ? (
            <Card className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground mt-4">লোড হচ্ছে...</p>
            </Card>
          ) : error ? (
            <Card className="p-8 text-center">
              <Icon name="AlertCircle" className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">ত্রুটি</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate('/communities')}>
                কমিউনিটিতে ফিরে যান
              </Button>
            </Card>
          ) : community ? (
            <Card className="p-8">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Users" className="w-10 h-10 text-primary" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {community.name}
                </h1>
                {community.description && (
                  <p className="text-gray-600 mb-4">
                    {community.description}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-700">
                    <Icon name="User" className="w-5 h-5" />
                    <span className="font-medium">তৈরি করেছেন:</span>
                  </div>
                  <span className="text-gray-900">{community.creator_name}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-700">
                    <Icon name="Users" className="w-5 h-5" />
                    <span className="font-medium">সদস্য সংখ্যা:</span>
                  </div>
                  <span className="text-gray-900">{community.member_count} জন</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-gray-700">
                    <Icon name="Globe" className="w-5 h-5" />
                    <span className="font-medium">ধরন:</span>
                  </div>
                  <span className="text-gray-900">
                    {community.is_public ? 'পাবলিক কমিউনিটি' : 'প্রাইভেট কমিউনিটি'}
                  </span>
                </div>
              </div>

              {community.is_member ? (
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 text-green-600 bg-green-50 px-6 py-4 rounded-lg mb-4">
                    <Icon name="Check" className="w-6 h-6" />
                    <span className="font-medium text-lg">আপনি ইতিমধ্যে এই কমিউনিটির সদস্য</span>
                  </div>
                  <Button onClick={() => navigate('/communities')} className="w-full">
                    কমিউনিটিতে ফিরে যান
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleJoinCommunity}
                  disabled={joining}
                  className="w-full text-lg py-6"
                >
                  {joining ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      যোগ দেওয়া হচ্ছে...
                    </>
                  ) : (
                    <>
                      <Icon name="UserPlus" className="w-5 h-5 mr-2" />
                      কমিউনিটিতে যোগ দিন
                    </>
                  )}
                </Button>
              )}
            </Card>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default JoinCommunity
